import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ticketsAPI } from "../services/api";
import {
  getAvatarColor,
  getAvatarImageSrc,
  getAvatarInitials,
  getUserIdentifier,
  hasProfilePicture,
} from "../utils/avatarUtils";
import { formatDate } from "../utils/dateFormatter";
import AttachmentPreview from "./AttachmentPreview";
import ReplyActionButton from "./ReplyActionButton";

const ReplyCard = ({
  reply,
  ticketId,
  allowDownload = true,
  canDelete = false,
  canAddAction = false,
  onDeleted,
  onActionsChanged,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState({ attachments: 0, actions: 0 });
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const getInitials = (replyItem) => {
    return getAvatarInitials(replyItem.user || replyItem);
  };

  const userName =
    getUserIdentifier(reply.user || reply) || t("common.unknown");
  const replyDate = reply.reply_date || reply.created_at || reply.date;
  const replyId = reply.reply_id || reply.id;

  const openDeleteDialog = async () => {
    setError("");
    setReason("");
    setDialogOpen(true);
    setIsLoadingPreview(true);
    try {
      const data = await ticketsAPI.getReplyDeletePreview(ticketId, replyId);
      setPreview({
        attachments: data.attachments || 0,
        actions: data.actions || 0,
      });
    } catch (err) {
      setError(err.message || t("ticketView.deleteReplyError"));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDelete = async () => {
    if (reason.trim().length < 5) {
      return;
    }
    setIsDeleting(true);
    setError("");
    try {
      await ticketsAPI.deleteTicketReply(ticketId, replyId, reason.trim());
      setDialogOpen(false);
      setSnackbar({
        open: true,
        message: t("ticketView.deleteReplySuccess"),
        severity: "success",
      });
      if (onDeleted) {
        onDeleted(replyId);
      }
    } catch (err) {
      setError(err.message || t("ticketView.deleteReplyError"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "grey.800" : "grey.50",
        "&:hover": {
          boxShadow: 2,
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Tooltip title={userName} arrow>
              <Avatar
                src={getAvatarImageSrc(reply.user || reply)}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: hasProfilePicture(reply.user || reply)
                    ? undefined
                    : getAvatarColor(reply.user || reply),
                  fontSize: "0.875rem",
                }}
              >
                {hasProfilePicture(reply.user || reply)
                  ? undefined
                  : getInitials(reply)}
              </Avatar>
            </Tooltip>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontWeight: "medium" }}
            >
              {userName}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {canDelete && (
              <Tooltip title={t("ticketView.deleteReply")}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={openDeleteDialog}
                  aria-label={t("ticketView.deleteReply")}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
              }}
            >
              {formatDate(replyDate)}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            lineHeight: 1.6,
            mb: reply.attachments && reply.attachments.length > 0 ? 2 : 0,
          }}
        >
          {reply.message || reply.content || t("common.noMessage")}
        </Typography>

        {reply.attachments && reply.attachments.length > 0 && (
          <AttachmentPreview
            attachments={reply.attachments}
            allowDownload={allowDownload}
          />
        )}

        {canAddAction && (
          <Box sx={{ mt: 2 }}>
            <ReplyActionButton
              reply={reply}
              ticketId={ticketId}
              count={reply.actions_count || 0}
              onSaved={onActionsChanged}
            />
          </Box>
        )}
      </CardContent>

      <Dialog
        open={dialogOpen}
        onClose={() => !isDeleting && setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>{t("ticketView.deleteReplyTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("ticketView.deleteReplyWarning")}
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            {isLoadingPreview
              ? t("common.loading")
              : t("ticketView.deleteReplyAlso", {
                  attachments: preview.attachments,
                  actions: preview.actions,
                })}
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            required
            multiline
            minRows={2}
            label={t("ticketView.deleteReplyReason")}
            helperText={t("ticketView.deleteReplyReasonHint")}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={isDeleting}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={isDeleting || reason.trim().length < 5}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default ReplyCard;
